import { useRef, useState } from "react";
import { useApp } from "@/context/AppContext";
import { fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Pencil, RotateCcw, Upload, Check, X } from "lucide-react";

/* Inline-editable TEXT. Renders `as` element; admin edit-mode enables click-to-edit. */
export function Editable({ id, as: Tag = "span", children, className = "", ...rest }) {
  const { editMode, ov, saveText, resetOverride } = useApp();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);

  const overridden = ov(id);
  const value = overridden !== undefined ? overridden : (typeof children === "string" ? children : "");
  const isOverridden = overridden !== undefined;

  if (!editMode) {
    return <Tag className={className} {...rest}>{value || children}</Tag>;
  }

  const commit = async () => {
    const text = ref.current?.innerText?.trim() ?? "";
    setEditing(false);
    if (text === (value || "").trim()) return;
    setSaving(true);
    try { await saveText(id, text); toast.success("Saved"); }
    catch (e) { toast.error("Save failed"); console.error(e); }
    finally { setSaving(false); }
  };

  const reset = async (e) => {
    e.stopPropagation();
    try { await resetOverride(id); toast.success("Reset to default"); }
    catch (err) { toast.error("Reset failed"); }
  };

  return (
    <Tag className={`${className} oj-editable ${editing ? "oj-editing" : ""}`} {...rest}
      data-editid={id}
      contentEditable={editing}
      suppressContentEditableWarning
      ref={ref}
      onClick={() => !editing && setEditing(true)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Escape") { setEditing(false); ref.current.innerText = value; } }}
    >
      {value || children}
      {!editing && (
        <span className="oj-edit-badge" contentEditable={false}>
          <Pencil className="w-3 h-3" />
          {isOverridden && <RotateCcw className="w-3 h-3 ml-1 hover:text-amber-300" onClick={reset} title="Reset to default" />}
        </span>
      )}
    </Tag>
  );
}

/* Inline-editable IMAGE. Admin edit-mode overlays Replace / Reset. */
export function EditableImage({ id, src, alt = "", className = "", imgClassName = "", ...rest }) {
  const { editMode, ov, saveImage, resetOverride } = useApp();
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const overridden = ov(id);
  const finalSrc = overridden !== undefined ? fileUrl(overridden) : src;

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try { await saveImage(id, file); toast.success("Image updated"); }
    catch (err) { toast.error("Upload failed"); console.error(err); }
    finally { setBusy(false); e.target.value = ""; }
  };

  if (!editMode) {
    return <img src={finalSrc} alt={alt} className={`${className} ${imgClassName}`} {...rest} />;
  }

  return (
    <div className={`relative group/edit ${className}`}>
      <img src={finalSrc} alt={alt} className={`w-full h-full ${imgClassName || "object-cover"}`} {...rest} />
      <div className="absolute inset-0 grid place-items-center gap-2 bg-black/40 opacity-0 group-hover/edit:opacity-100 transition-opacity z-20 pointer-events-none">
        <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
          className="pointer-events-auto inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white text-[#017471] text-xs font-bold shadow-lg" data-testid={`edit-img-${id}`}>
          <Upload className="w-3.5 h-3.5" /> {busy ? "Uploading…" : "Replace"}
        </button>
        {overridden !== undefined && (
          <button type="button" onClick={() => resetOverride(id)} className="pointer-events-auto inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-500 text-[#014845] text-xs font-bold shadow-lg">
            <RotateCcw className="w-3.5 h-3.5" /> Reset
          </button>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onFile} />
    </div>
  );
}
